import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { SiteNav } from "@/components/SiteNav";
import { Search, Store, ArrowRight, ShoppingBag, UserPlus, Star, Sparkles } from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [storesRes, productsRes] = await Promise.all([
        fetch(`${API}/public/stores`),
        fetch(`${API}/public/products`)
      ]);
      if (storesRes.ok) setStores(await storesRes.json());
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(Array.isArray(data) ? data : data.products || []);
      }
    } catch (e) {
      console.error("Failed to fetch:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/store?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http')) return url;
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteNav active="home" />

      {/* Hero — Light with subtle indigo glow */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-slate-50">
        {/* Soft accent glows */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-indigo-200/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-violet-200/40 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-8">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            <span className="text-indigo-700 text-sm font-medium">A premium UK marketplace</span>
          </div>

          <h1 className="font-brand text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tighter mb-6 leading-[0.9]">
            DISCOVER.
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-600 bg-clip-text text-transparent">EXPLORE.</span>
            <br />
            SHOP.
          </h1>
          <p className="text-slate-600 text-lg max-w-xl mx-auto mb-12">
            A marketplace unlike any other. Verified merchants. Curated products. Seamless experience.
          </p>

          {/* Light Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-2xl blur opacity-15 group-hover:opacity-30 transition-opacity" />
              <div className="relative flex items-center rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                <Search className="h-5 w-5 text-slate-400 ml-5 flex-shrink-0" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Discover stores, products, and more..."
                  className="flex-1 bg-transparent border-0 text-slate-900 text-base h-14 pl-4 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="home-search-input"
                />
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-500 text-white h-10 px-6 mr-2 rounded-xl" data-testid="home-search-btn">
                  Search
                </Button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-6 pb-20">

        {/* Trending Products */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
              <Star className="h-6 w-6 text-indigo-600" />
              TRENDING NOW
            </h2>
            <Link to="/store" className="text-indigo-600 text-sm hover:text-indigo-700 transition-colors flex items-center gap-1 font-medium">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {products.slice(0, 5).map((product) => {
              const img = getImageUrl(product.image_url);
              return (
                <Link
                  key={product.product_id}
                  to={`/product/${product.product_id}`}
                  className="group relative rounded-xl overflow-hidden bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all duration-300"
                  data-testid={`product-${product.product_id}`}
                >
                  <div className="aspect-square overflow-hidden bg-slate-100">
                    {img ? (
                      <img src={img} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-slate-700 text-xs truncate group-hover:text-slate-900 transition-colors">{product.name}</p>
                    <p className="text-slate-900 font-bold text-sm mt-0.5">£{product.price?.toFixed(2)}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Stores — Bento Grid */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
              <Store className="h-6 w-6 text-indigo-600" />
              OUR STORES
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[180px]">
            {stores.map((store, idx) => {
              const logo = getImageUrl(store.logo_url);
              const isLarge = idx === 0;
              return (
                <Link
                  key={store.user_id}
                  to={`/store/${store.user_id}`}
                  className={`group relative rounded-xl bg-white border border-slate-200 overflow-hidden hover:border-indigo-300 hover:shadow-lg transition-all duration-300 ${isLarge ? 'col-span-2 row-span-2' : ''}`}
                  data-testid={`store-${store.user_id}`}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-violet-50/0 group-hover:from-indigo-50 group-hover:to-violet-50/50 transition-all duration-500" />

                  <div className="relative h-full p-6 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className={`${isLarge ? 'w-16 h-16' : 'w-12 h-12'} rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden`}>
                        {logo ? (
                          <img src={logo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Store className="h-5 w-5 text-indigo-600" />
                        )}
                      </div>
                      <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-full border border-slate-200">
                        {store.product_count || 0} products
                      </span>
                    </div>
                    <div>
                      <h3 className={`font-semibold text-slate-900 group-hover:text-indigo-700 transition-colors ${isLarge ? 'text-xl' : 'text-sm'}`}>
                        {store.business_name || store.name}
                      </h3>
                      {store.description && isLarge && (
                        <p className="text-slate-500 text-sm mt-1 line-clamp-2">{store.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-indigo-600 text-xs font-medium group-hover:text-indigo-700 flex items-center gap-1">
                          Visit Store <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* Become a Seller Bento Card — accent tile */}
            <div className="col-span-2 relative rounded-xl overflow-hidden border border-indigo-200 bg-gradient-to-br from-indigo-600 to-violet-700 text-white">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-[60px]" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-violet-400/20 rounded-full blur-[40px]" />
              <div className="relative h-full p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-indigo-200" />
                    <span className="text-indigo-100 text-xs font-medium uppercase tracking-wider">Join the marketplace</span>
                  </div>
                  <h3 className="font-brand text-xl font-bold tracking-tight">
                    Sell on <span className="text-indigo-200">VEYRONIX</span>
                  </h3>
                  <p className="text-indigo-100 text-sm mt-2 max-w-sm">
                    List your products, reach thousands. AI-powered tools. Only 5% commission.
                  </p>
                </div>
                <Button onClick={() => navigate("/register")} className="bg-white text-indigo-700 hover:bg-indigo-50 w-fit mt-4 font-semibold" data-testid="become-seller-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Register as Seller
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* More Products */}
        {products.length > 5 && (
          <section className="mb-16">
            <h2 className="font-brand text-2xl font-bold text-slate-900 tracking-tight mb-6 flex items-center gap-3">
              <ShoppingBag className="h-6 w-6 text-indigo-600" />
              MORE TO EXPLORE
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {products.slice(5).map((product) => {
                const img = getImageUrl(product.image_url);
                return (
                  <Link
                    key={product.product_id}
                    to={`/product/${product.product_id}`}
                    className="group relative rounded-xl overflow-hidden bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                      {img ? (
                        <img src={img} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="h-8 w-8 text-slate-300" /></div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-slate-600 text-xs truncate group-hover:text-slate-900">{product.name}</p>
                      <p className="text-slate-900 font-bold text-sm">£{product.price?.toFixed(2)}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Footer — Dark for premium contrast */}
      <footer className="border-t border-slate-200 bg-slate-950 py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-8">
            <span className="font-brand text-6xl font-bold text-white/[0.04] tracking-tighter select-none">VEYRONIX</span>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="V" className="h-7 w-auto brightness-0 invert opacity-60" />
              <span className="text-slate-500 font-brand text-sm font-bold">VEYRONIX</span>
            </div>
            <div className="flex items-center gap-6 text-xs text-slate-500">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <a href="https://crm.veyronix.co.uk" className="hover:text-white transition-colors">CRM</a>
              <Link to="/commerce" className="hover:text-white transition-colors">Commerce</Link>
              <Link to="/pulse" className="hover:text-white transition-colors" data-testid="footer-pulse">PULSE</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/register" className="hover:text-white transition-colors">Become a Seller</Link>
            </div>
            <p className="text-slate-600 text-xs">© {new Date().getFullYear()} Veyronix Limited · Company no. 16821530</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
